import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { formatDate, formatQty, formatTime } from "@/lib/format";
import { Card, PageHeader, Select, Badge } from "@/components/ui-kit";
import { useTableFilter } from "@/hooks/useTableFilter";
import { usePagination } from "@/hooks/usePagination";
import { TrendingDown, TrendingUp, X } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";

const SOURCE_LABELS = {
  initial: "Stok Awal",
  manual: "Penyesuaian Manual",
  upsert: "Edit Stok",
  sj_create: "SJ Baru",
  sj_update_restore: "Edit SJ (kembalikan)",
  sj_update_apply: "Edit SJ (terapkan)",
  sj_delete: "Hapus SJ",
  damage: "Material Rusak",
};

function startOfYearISO(d) { return new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10); }
function startOfMonthISO(d) { return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }
function startOfWeekISO(d) {
  const day = d.getDay() || 7; // Monday-based
  const x = new Date(d);
  x.setDate(d.getDate() - (day - 1));
  return x.toISOString().slice(0, 10);
}
function todayISO(d) { return d.toISOString().slice(0, 10); }

export default function StockLog() {
  const { t } = useLang(); // eslint-disable-line no-unused-vars
  const today = useMemo(() => new Date(), []);
  const [period, setPeriod] = useState("month"); // day | week | month | year | custom
  const [start, setStart] = useState(startOfMonthISO(today));
  const [end, setEnd] = useState(todayISO(today));
  const [materialFilter, setMaterialFilter] = useState("");
  const [stocks, setStocks] = useState([]);
  const [rows, setRows] = useState([]);
  const { filtered: textFiltered, FilterBar } = useTableFilter(rows, ["material_name", "code", "note", "source", "user_email", "user_name"], "Cari log stok…");
  const filtered = materialFilter ? textFiltered.filter((r) => r.material_name === materialFilter) : textFiltered;

  const applyPreset = (p) => {
    setPeriod(p);
    const t0 = new Date();
    if (p === "day") setStart(todayISO(t0));
    else if (p === "week") setStart(startOfWeekISO(t0));
    else if (p === "month") setStart(startOfMonthISO(t0));
    else if (p === "year") setStart(startOfYearISO(t0));
    setEnd(todayISO(t0));
  };

  const load = async () => {
    const q = new URLSearchParams();
    if (start) q.append("start", start);
    if (end) q.append("end", end);
    if (materialFilter) q.append("material_name", materialFilter);
    const [r, sr] = await Promise.all([api.get(`/stock-movements?${q.toString()}`), api.get("/stocks")]);
    setRows(r.data);
    setStocks(sr.data);
  };
  // Iter v34{aj} — Auto-reload on filter change (matches SJ page's live filter UX).
  // Debounce lightly so rapid typing doesn't spam the backend.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end, materialFilter]);

  // Aggregate totals
  const totalIn = filtered.filter((r) => r.delta > 0).reduce((s, r) => s + r.delta, 0);
  const totalOut = filtered.filter((r) => r.delta < 0).reduce((s, r) => s + Math.abs(r.delta), 0);
  // Iter v28 #1 — 25 rows per page
  const { pageItems, Pagination } = usePagination(filtered, { perPage: 25 });

  return (
    <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <PageHeader
        overline="Inventory · Audit"
        title="Log Update Stok Barang"
        description="Riwayat lengkap setiap perubahan stok — otomatis dari Surat Jalan & manual."
      />

      {/* Iter v34{aj} — Filter bar matches SJ style: inline Dari—Sampai with X reset,
          live-update (no separate Terapkan button). */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {FilterBar}
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={period} onChange={(e) => applyPreset(e.target.value)} className="!h-9 !text-xs" data-testid="log-period">
            <option value="day">Hari Ini</option>
            <option value="week">Minggu Ini</option>
            <option value="month">Bulan Ini</option>
            <option value="year">Tahun Ini</option>
            <option value="custom">Custom</option>
          </Select>
          <div className="flex items-center gap-1">
            <DatePicker value={start} onChange={(e) => { setStart(e.target.value); setPeriod("custom"); }} className="!w-auto !text-xs" placeholder="Dari" data-testid="log-start" title="Tanggal dari" />
            <span className="text-stone-400 text-xs">—</span>
            <DatePicker value={end} onChange={(e) => { setEnd(e.target.value); setPeriod("custom"); }} className="!w-auto !text-xs" placeholder="Sampai" data-testid="log-end" title="Tanggal sampai" />
            {(start || end) && (
              <button onClick={() => { setStart(""); setEnd(""); setPeriod("custom"); }} className="text-stone-400 hover:text-stone-700 text-xs px-1" title="Reset tanggal" data-testid="log-date-reset">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Select value={materialFilter} onChange={(e) => setMaterialFilter(e.target.value)} className="!h-9 !text-xs" data-testid="log-material">
            <option value="">Semua Material</option>
            {stocks.map((s) => <option key={s.id} value={s.material_name}>{s.material_name}{s.code ? ` [${s.code}]` : ""}</option>)}
          </Select>
          <div className="text-xs text-stone-600 flex flex-col gap-0.5 font-mono-num pl-2 border-l border-stone-200">
            <span className="text-emerald-700"><TrendingUp className="inline h-3 w-3 mr-1" />Masuk: +{formatQty(totalIn)}</span>
            <span className="text-red-700"><TrendingDown className="inline h-3 w-3 mr-1" />Keluar: -{formatQty(totalOut)}</span>
          </div>
        </div>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
              <th className="py-3 px-3">Waktu</th>
              <th className="py-3 px-3">Kode</th>
              <th className="py-3 px-3">Material</th>
              <th className="py-3 px-3">Satuan</th>
              <th className="py-3 px-3 text-right">Perubahan</th>
              <th className="py-3 px-3 text-right">Sisa</th>
              <th className="py-3 px-3">Sumber</th>
              <th className="py-3 px-3">Catatan</th>
              <th className="py-3 px-3">Oleh</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="py-12 text-center text-stone-400">Belum ada log untuk periode ini.</td></tr>
            )}
            {pageItems.map((r, idx) => (
              <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`log-row-${idx}`}>
                <td className="py-2.5 px-3 font-mono text-stone-600">
                  {formatDate(r.ts)} <span className="text-stone-400">{formatTime(r.ts)}</span>
                  {/* Iter v34cu (#3) — tanggal riil stok masuk bila berbeda dari waktu pencatatan. */}
                  {r.entry_date && r.entry_date !== String(r.ts).slice(0, 10) && (
                    <div className="text-[10px] text-emerald-700" data-testid={`log-entry-date-${idx}`}>
                      Tgl masuk: {formatDate(r.entry_date)}
                    </div>
                  )}
                </td>
                <td className="py-2.5 px-3 font-mono text-stone-700">{r.code || <span className="text-stone-300">—</span>}</td>
                <td className="py-2.5 px-3 font-medium">{r.material_name}</td>
                <td className="py-2.5 px-3 text-stone-600">{r.unit}</td>
                <td className={`py-2.5 px-3 text-right font-mono-num font-semibold ${r.delta >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {r.delta >= 0 ? "+" : ""}{formatQty(r.delta)}
                </td>
                <td className="py-2.5 px-3 text-right font-mono-num text-stone-700">{formatQty(r.balance_after)}</td>
                <td className="py-2.5 px-3"><Badge tone={r.source === "damage" ? "danger" : r.source?.startsWith("sj_") ? "info" : "terra"}>{SOURCE_LABELS[r.source] || r.source}</Badge></td>
                <td className="py-2.5 px-3 text-stone-600 max-w-xs">
                  {r.source === "damage" ? (
                    <div className="space-y-1">
                      <div className="text-stone-700 break-words" title={r.chronology || r.note}>{r.chronology || r.note}</div>
                      {r.photo_file_id && (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.photo_file_id}`}
                          target="_blank" rel="noopener noreferrer"
                          className="inline-block"
                          data-testid={`log-damage-photo-${idx}`}
                        >
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.photo_file_id}`}
                            alt="Foto kerusakan"
                            className="h-12 w-12 object-cover border border-stone-200"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        </a>
                      )}
                      {r.photo_taken_at && (
                        <div className="text-[10px] text-stone-500 font-mono">Foto: {formatDate(r.photo_taken_at)} {formatTime(r.photo_taken_at)} WIB</div>
                      )}
                    </div>
                  ) : (
                    <span className="truncate inline-block max-w-xs" title={r.note}>{r.note || "—"}</span>
                  )}
                </td>
                <td className="py-2.5 px-3 text-stone-600">{r.user_name || r.user_email || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {Pagination}
      </Card>
    </div>
  );
}
